import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SLUG_PATTERN, VILLAGE_SLUG_MAX_LENGTH, normSlug, slugBlur } from '@/lib/slug';
import { api, type AdminVillage } from '@/lib/api';
import { TranslationsField } from '@/components/TranslationsField';
import { LocationPickerMap } from '@/components/LocationPickerMap';
import { ImageUploadField } from '@/components/ImageUploadField';
import { VillageImagesSection } from '@/components/VillageImagesSection';
import { ExperiencesSection } from '@/components/ExperiencesSection';
import { VillageBusinessesSection } from '@/components/VillageBusinessesSection';

export const Route = createFileRoute('/_authed/villages/$villageId')({
  component: VillageDetailPage,
});

const tr = z.object({
  en: z.string().min(1),
  el: z.string().optional(),
  es: z.string().optional(),
  de: z.string().optional(),
  it: z.string().optional(),
  ru: z.string().optional(),
  he: z.string().optional(),
  pl: z.string().optional(),
  uk: z.string().optional(),
  fr: z.string().optional(),
  ar: z.string().optional(),
});
// `story` is the long text on the public community page; unlike name and
// description it may legitimately be empty while the village is being written
// up, so English is optional here.
const storyTr = tr.extend({ en: z.string() });

const schema = z.object({
  slug: z.preprocess(
    normSlug(VILLAGE_SLUG_MAX_LENGTH),
    z
      .string()
      .min(1, 'Needs at least one letter or digit')
      .max(VILLAGE_SLUG_MAX_LENGTH, `At most ${VILLAGE_SLUG_MAX_LENGTH} characters`)
      .regex(SLUG_PATTERN, 'Lowercase letters, digits and single hyphens'),
  ),
  name: tr,
  description: tr,
  story: storyTr,
  heroImageUrl: z.string().url().or(z.literal('')).nullable(),
  lat: z.coerce.number(),
  lng: z.coerce.number(),
});

type FormValues = z.infer<typeof schema>;

function VillageDetailPage() {
  const { villageId } = Route.useParams();
  const queryClient = useQueryClient();

  const villageQ = useQuery({
    queryKey: ['village', villageId],
    queryFn: () => api.getVillage(villageId),
  });

  const save = useMutation({
    mutationFn: (body: Partial<AdminVillage>) => api.updateVillage(villageId, body),
    onSuccess: (data) => {
      queryClient.setQueryData(['village', villageId], data);
      queryClient.invalidateQueries({ queryKey: ['villages'] });
    },
  });

  if (villageQ.isLoading || !villageQ.data) {
    return <div className="card text-slate-gray">Loading village…</div>;
  }

  const village = villageQ.data.village;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/villages" className="btn-ghost -ml-3 mb-2">
          ← Back to villages
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-h1">{village.name.en}</h1>
            <p className="mt-1 text-caption text-slate-gray">
              Last updated {new Date(village.updatedAt).toLocaleString()}
            </p>
          </div>
          {/*
           * Export the village's complete current text in every locale.
           * Plain links rather than fetch + blob: the API sets
           * Content-Disposition, and a top-level GET carries the admin
           * session cookie, exactly like the analytics CSV exports.
           */}
          <div className="flex shrink-0 items-center gap-2">
            <a
              href={api.villageExportUrl(villageId, 'md')}
              className="btn-primary"
              title="Download every text of this village (all languages) as Markdown"
            >
              Export village
            </a>
            <a
              href={api.villageExportUrl(villageId, 'json')}
              className="btn-ghost"
              title="Same content as JSON, for backup or diffing"
            >
              JSON
            </a>
            {/* Visitor statistics for this village alone. The all-villages
                version lives on the Analytics page, where the date filter is. */}
            <a
              href={api.demographicsCsvUrl({ villageId })}
              className="btn-ghost"
              download
              title="Visitor demographics for this village as CSV (all time)"
            >
              Demographics CSV
            </a>
          </div>
        </div>
      </div>

      <VillageForm
        village={village}
        onSubmit={(body) => save.mutateAsync(body)}
        saving={save.isPending}
        error={save.error ? String(save.error) : null}
        saved={save.isSuccess}
      />

      <VillageImagesSection villageId={villageId} />

      <ExperiencesSection villageId={villageId} />

      <VillageBusinessesSection villageId={villageId} />

      <RoutesSection villageId={villageId} />
    </div>
  );
}

const routeCreateSchema = z.object({
  name: tr,
});
type RouteCreateValues = z.infer<typeof routeCreateSchema>;

function RoutesSection({ villageId }: { villageId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const routesQ = useQuery({
    queryKey: ['routes', villageId],
    queryFn: () => api.listRoutes(villageId),
  });

  const create = useMutation({
    mutationFn: (v: RouteCreateValues) =>
      api.createRoute({ villageId, name: v.name }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['routes', villageId] });
      setCreating(false);
      navigate({ to: '/routes/$routeId', params: { routeId: data.route.id } });
    },
  });

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-h2">Routes</h2>
        {!creating ? (
          <button className="btn-secondary" onClick={() => setCreating(true)}>
            + New route
          </button>
        ) : null}
      </div>

      {creating ? (
        <CreateRouteForm
          onSubmit={(v) => create.mutateAsync(v)}
          submitting={create.isPending}
          error={create.error ? String(create.error) : null}
          onCancel={() => setCreating(false)}
        />
      ) : null}

      {routesQ.isLoading ? (
        <div className="card text-slate-gray">Loading routes…</div>
      ) : (
        <div className="grid gap-3">
          {routesQ.data?.routes.map((r) => (
            <Link
              key={r.id}
              to="/routes/$routeId"
              params={{ routeId: r.id }}
              className="card flex items-center justify-between hover:border-tech-blue/40"
            >
              <div>
                <p className="font-semibold text-dark-navy">{r.name.en}</p>
                <p className="text-caption text-slate-gray">{r.name.el}</p>
              </div>
              <span className="text-caption text-slate-gray">→</span>
            </Link>
          ))}
          {routesQ.data && routesQ.data.routes.length === 0 && !creating ? (
            <div className="card text-slate-gray">
              No routes yet. Click <strong>+ New route</strong> to add one.
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

function CreateRouteForm({
  onSubmit,
  submitting,
  error,
  onCancel,
}: {
  onSubmit: (v: RouteCreateValues) => Promise<unknown>;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
}) {
  const { register, handleSubmit, setValue, watch } = useForm<RouteCreateValues>({
    resolver: zodResolver(routeCreateSchema),
    defaultValues: { name: { en: '' } },
  });
  return (
    <form
      onSubmit={handleSubmit((v) => onSubmit(v))}
      className="card mb-4 space-y-4"
    >
      <h3 className="text-h3">New route</h3>
      <TranslationsField
        label="Route name"
        required
        basePath="name"
        register={register}
        setValue={setValue}
        watch={watch}
      />
      {error ? (
        <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-caption text-error">
          {error}
        </div>
      ) : null}
      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create route'}
        </button>
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function VillageForm({
  village,
  onSubmit,
  saving,
  error,
  saved,
}: {
  village: AdminVillage;
  onSubmit: (body: Partial<AdminVillage>) => Promise<unknown>;
  saving: boolean;
  error: string | null;
  saved: boolean;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      slug: village.slug,
      name: village.name,
      description: village.description,
      story: village.story ?? { en: '' },
      heroImageUrl: village.heroImageUrl ?? '',
      lat: village.lat,
      lng: village.lng,
    },
  });
  const lat = watch('lat');
  const lng = watch('lng');

  const submit = async (values: FormValues) => {
    // Empty strings would publish an empty <section> on the village page; null
    // is what "not written yet" looks like to the site.
    await onSubmit({
      ...values,
      story: values.story.en.trim() ? values.story : null,
      heroImageUrl: values.heroImageUrl ? values.heroImageUrl : null,
    });
    reset(values);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="card space-y-5">
      <div>
        <label className="label">Slug</label>
        <input className="input font-mono" {...register('slug', slugBlur(setValue, VILLAGE_SLUG_MAX_LENGTH))} />
        {errors.slug ? (
          <p className="mt-1 text-caption text-error">{errors.slug.message}</p>
        ) : null}
      </div>

      <TranslationsField
        label="Name"
        required
        basePath="name"
        register={register}
        setValue={setValue}
        watch={watch}
      />

      <TranslationsField
        label="Description"
        multiline
        required
        basePath="description"
        register={register}
        setValue={setValue}
        watch={watch}
      />

      <TranslationsField
        label="Story — public community page (history, character, what is worth stopping for)"
        multiline
        basePath="story"
        register={register}
        setValue={setValue}
        watch={watch}
      />

      <ImageUploadField
        label="Hero photo — public community page"
        value={watch('heroImageUrl')}
        onChange={(url) => setValue('heroImageUrl', url, { shouldDirty: true })}
        resourceType="places"
        helper="Wide landscape shot. The gallery below is everything else."
      />

      <LocationPickerMap
        label="Village centre"
        value={{ lat: Number(lat) || 0, lng: Number(lng) || 0 }}
        onChange={(next) => {
          setValue('lat', next.lat, { shouldDirty: true });
          setValue('lng', next.lng, { shouldDirty: true });
        }}
      />

      {error ? (
        <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-caption text-error">
          {error}
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={!isDirty || saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && !isDirty ? (
          <span className="text-caption text-success">Saved ✓</span>
        ) : null}
      </div>
    </form>
  );
}
